import { createContext, useContext, type Accessor, type JSX, type ParentProps } from "solid-js";

import styles from "./art.module.css";

export type ArtDefId =
  | "g-enamel"
  | "g-root"
  | "g-dentin"
  | "g-pulp"
  | "g-pulp-hot"
  | "g-pulp-dead"
  | "g-gum"
  | "g-gum-hot"
  | "p-bone"
  | "g-decay"
  | "g-amalgam"
  | "g-porcelain"
  | "g-composite"
  | "g-core"
  | "g-gutta"
  | "g-steel"
  | "g-tartar"
  | "g-hot-glow"
  | "g-occ"
  | "g-vignette"
  | "m-vignette"
  | "f-blur3"
  | "f-blur6"
  | "m-cusp"
  | "m-break"
  | "c-root"
  | "c-panel"
  | "g-whiten";

// The ids live in the document's one id namespace, so every viewer prefixes its own copy: two decks on a page (the
// preview and the presenter) never resolve each other's defs, and unmounting one never blanks the other.
const ArtPrefix = createContext<Accessor<string>>(() => "tp-art");

function defId(prefix: string, id: ArtDefId): string {
  return `${prefix}-${id}`;
}

/**
 * The drawings' gradients, patterns, masks, clips and filters, once per viewer. A zero-size svg rather than
 * `display: none`, which stops Chrome and Firefox from painting the gradients it holds.
 */
export function ArtDefs(props: { prefix: string }): JSX.Element {
  const id = (name: ArtDefId) => defId(props.prefix, name);
  return (
    <svg class={styles["svg-defs"]} width="0" height="0" aria-hidden="true">
      <defs>
        <linearGradient id={id("g-enamel")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#FFFFFF" />
          <stop offset=".62" stop-color="#F8F4EC" />
          <stop offset="1" stop-color="#ECE3D3" />
        </linearGradient>
        <linearGradient id={id("g-root")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#F3EBDD" />
          <stop offset="1" stop-color="#E2D3B9" />
        </linearGradient>
        <linearGradient id={id("g-dentin")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#F6ECD8" />
          <stop offset="1" stop-color="#E7D7B8" />
        </linearGradient>
        <radialGradient id={id("g-pulp")} cx=".5" cy=".22" r=".9">
          <stop offset="0" stop-color="#FCCDC3" />
          <stop offset="1" stop-color="#EC8C80" />
        </radialGradient>
        <radialGradient id={id("g-pulp-hot")} cx=".5" cy=".22" r=".9">
          <stop offset="0" stop-color="#FF7B66" />
          <stop offset="1" stop-color="#C02E1D" />
        </radialGradient>
        <linearGradient id={id("g-pulp-dead")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#A39B90" />
          <stop offset="1" stop-color="#6C655D" />
        </linearGradient>
        <linearGradient id={id("g-gum")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#F8C9C4" />
          <stop offset="1" stop-color="#EBA39D" />
        </linearGradient>
        <linearGradient id={id("g-gum-hot")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#F49B91" />
          <stop offset="1" stop-color="#D96C66" />
        </linearGradient>
        <pattern id={id("p-bone")} width="12" height="12" patternUnits="userSpaceOnUse">
          <rect width="12" height="12" fill="#F1E6D0" />
          <circle cx="3" cy="4" r="1.7" fill="#E4D4B4" />
          <circle cx="9" cy="9" r="1.3" fill="#E4D4B4" />
          <circle cx="10" cy="2.5" r=".9" fill="#E9DCC3" />
        </pattern>
        <radialGradient id={id("g-decay")} cx=".5" cy=".42" r=".62">
          <stop offset="0" stop-color="#2A170B" />
          <stop offset=".6" stop-color="#57371C" />
          <stop offset="1" stop-color="#8E6844" />
        </radialGradient>
        <linearGradient id={id("g-amalgam")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#D3D9DE" />
          <stop offset=".45" stop-color="#97A1A9" />
          <stop offset="1" stop-color="#69737B" />
        </linearGradient>
        <linearGradient id={id("g-porcelain")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#FFFFFF" />
          <stop offset="1" stop-color="#E4EEF0" />
        </linearGradient>
        <linearGradient id={id("g-composite")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#F7EEDC" />
          <stop offset="1" stop-color="#EADBBE" />
        </linearGradient>
        <linearGradient id={id("g-core")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#C7DAE6" />
          <stop offset="1" stop-color="#8DAEC4" />
        </linearGradient>
        <linearGradient id={id("g-gutta")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#F4B06A" />
          <stop offset="1" stop-color="#D7752A" />
        </linearGradient>
        <linearGradient id={id("g-steel")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#7C8890" />
          <stop offset=".5" stop-color="#E7ECEF" />
          <stop offset="1" stop-color="#6B7780" />
        </linearGradient>
        <linearGradient id={id("g-tartar")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#DCC184" />
          <stop offset="1" stop-color="#B08A48" />
        </linearGradient>
        <radialGradient id={id("g-hot-glow")} cx=".5" cy=".5" r=".5">
          <stop offset="0" stop-color="#E5412F" stop-opacity=".8" />
          <stop offset="1" stop-color="#E5412F" stop-opacity="0" />
        </radialGradient>
        <radialGradient id={id("g-occ")} cx=".42" cy=".36" r=".78">
          <stop offset="0" stop-color="#FFFFFF" />
          <stop offset="1" stop-color="#E9E0D1" />
        </radialGradient>
        <radialGradient id={id("g-vignette")} cx=".5" cy=".3" r=".66">
          <stop offset=".62" stop-color="#FFFFFF" />
          <stop offset="1" stop-color="#000000" />
        </radialGradient>
        {/* Warm on the left fading to clear: a crown shading from stained to white. */}
        <linearGradient id={id("g-whiten")} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#E2C37F" stop-opacity=".78" />
          <stop offset=".5" stop-color="#EBD7A6" stop-opacity=".38" />
          <stop offset="1" stop-color="#FFFFFF" stop-opacity="0" />
        </linearGradient>
        <mask id={id("m-vignette")} maskContentUnits="objectBoundingBox">
          <rect width="1" height="1" fill={`url(#${id("g-vignette")})`} />
        </mask>
        <filter id={id("f-blur3")} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <filter id={id("f-blur6")} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
        <mask id={id("m-cusp")} maskUnits="userSpaceOnUse" x="-30" y="-30" width="180" height="240">
          <rect x="-30" y="-30" width="180" height="240" fill="#FFFFFF" />
          <path d="M74,4 L124,4 L124,80 L98,78 L84,70 L88,52 L78,44 L74,30 Z" fill="#000000" />
        </mask>
        <mask
          id={id("m-break")}
          maskUnits="userSpaceOnUse"
          x="-30"
          y="-30"
          width="180"
          height="240"
        >
          <rect x="-30" y="-30" width="180" height="240" fill="#FFFFFF" />
          <path d="M60,4 L124,4 L124,80 L98,78 L66,92 L70,66 L58,52 L60,30 Z" fill="#000000" />
        </mask>
        <clipPath id={id("c-root")}>
          <rect x="-10" y="79" width="140" height="120" />
        </clipPath>
        <clipPath id={id("c-panel")}>
          <rect x="-118" y="-44" width="236" height="318" rx="26" />
        </clipPath>
      </defs>
    </svg>
  );
}

/** Points every drawing below it at the ArtDefs rendered with the same prefix. */
export function ArtIdProvider(props: ParentProps<{ prefix: string }>): JSX.Element {
  const prefix = () => props.prefix;
  return <ArtPrefix.Provider value={prefix}>{props.children}</ArtPrefix.Provider>;
}

/** `url(#<prefix>-<id>)` for a fill, stroke, mask, clip-path or filter attribute. */
export function useArtRef(id: ArtDefId): string {
  return `url(#${defId(useContext(ArtPrefix)(), id)})`;
}
